import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === "paid") {
          setStatus("success");
          setPaymentInfo(data);
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        setStatus("error");
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  const formatPrice = (cents) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
    }).format(cents / 100);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-white" data-testid="payment-success-page">
      <div className="page-container max-w-lg text-center">
        {status === "checking" && (
          <div className="animate-fade-in-up">
            <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-6 animate-spin" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Vérification du paiement...
            </h1>
            <p className="text-gray-600">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Paiement réussi !
            </h1>
            <p className="text-gray-600 mb-8">
              Félicitations ! Votre achat a été confirmé. Notre équipe vous 
              contactera très prochainement pour organiser la livraison de votre véhicule.
            </p>
            
            {paymentInfo && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8 text-left">
                <h3 className="text-gray-900 font-semibold mb-4">Récapitulatif</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Véhicule</span>
                    <span className="text-gray-900">{paymentInfo.metadata?.car_name || "Véhicule"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Montant</span>
                    <span className="text-yellow-600 font-bold">
                      {formatPrice(paymentInfo.amount_total)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button className="bg-yellow-500 text-white hover:bg-yellow-600 font-bold uppercase">
                  Retour à l'accueil
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-gray-300 text-gray-700 hover:border-yellow-500 hover:text-yellow-600">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        )}

        {(status === "error" || status === "expired" || status === "timeout") && (
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {status === "expired" ? "Session expirée" : "Erreur de paiement"}
            </h1>
            <p className="text-gray-600 mb-8">
              {status === "expired" 
                ? "Votre session de paiement a expiré. Veuillez réessayer."
                : status === "timeout"
                ? "La vérification du paiement a pris trop de temps. Veuillez nous contacter si vous avez été débité."
                : "Une erreur s'est produite lors du paiement. Veuillez réessayer ou nous contacter."
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/voitures">
                <Button className="bg-yellow-500 text-white hover:bg-yellow-600 font-bold uppercase">
                  Retour aux véhicules
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-gray-300 text-gray-700 hover:border-yellow-500 hover:text-yellow-600">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
